// New-name slot of a RENAME statement.
//
// `ALTER TABLE t RENAME TO ▮`, `ALTER TABLE t RENAME COLUMN c TO ▮`, MySQL's
// `RENAME TABLE a TO ▮` / `ALTER TABLE t RENAME AS ▮`, Oracle's standalone
// `RENAME a TO ▮` — every one of them puts the caret on a name that does NOT
// exist yet. Suggesting an existing table there is actively wrong: the rename
// fails ("table already exists") for exactly the names the schema can offer.
//
// Before this module the caret fell to `unknown` and answered with the whole
// table list (sqlite script.35 §6; pgsql script.38 §3; oracle script.38 §3 all
// pin the same leak). Like the savepoint-name slots (`savepointSlot.ts`) this
// is a CLOSED slot: it is claimed even though nothing can be offered, because
// an empty widget is the honest answer and the relation flood it replaces was
// never a legal completion.
//
// Cross-engine: sqlite / pgsql / mysql / oracle all spell a rename with a
// `… RENAME … TO <new-name>` tail, so one recognizer covers them. MSSQL is the
// exception and is deliberately NOT wired: T-SQL has no RENAME statement — it
// renames through `EXEC sp_rename 'old', 'new'`, whose arguments are string
// literals, not an identifier slot.

import type { DatabaseEngineId } from '@/core/engines';

import { tokenize, type Token } from './tokenizer';

/**
 * True when the caret sits on the NEW name of a rename: the statement carries
 * a `RENAME` keyword and the last completed word before the caret is the `TO`
 * (MySQL also spells it `AS`) that introduces the new name. A partially typed
 * name still counts — the slot stays closed while the user types it.
 */
export function isAtRenameTargetSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  const tokens = tokenize(text, statementStart, offset, engine);
  let end = tokens.length;
  // A name token that touches the caret is the one being typed, not a
  // completed keyword — drop it before reading the tail.
  const lastTok = tokens[end - 1];
  if (lastTok && lastTok.end >= offset && lastTok.type === 'ident') end -= 1;
  if (end < 2) return false;
  const tail = wordOf(tokens[end - 1]);
  const introducesName = tail === 'TO' || (engine === 'mysql' && tail === 'AS');
  if (!introducesName) return false;
  for (let i = 0; i < end - 1; i += 1) {
    if (wordOf(tokens[i]) === 'RENAME') return true;
  }
  return false;
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
